import './index.scss';
import { LastLocationCellRenderer } from '@components';
import { Api, authService } from '@services';
import Axios from 'axios';
import { useEffect, useState } from 'react';
import { LinkCellRenderer } from '../../../../components';
import {
  PagedQueryParams,
  ServerSideGrid,
} from '../../../../components/grids/AgGrid/ServerSideGrid';
import { handleTimeoutApiError } from '../../../../utils/handleTimeoutApiError';
import AssetNameCellRenderer from '../../../deviceMgmt/AssetNameCellRenderer';
import {
  assetColumns,
  batteryColumns,
  environmentalColumns,
  gatewayNetworkColumns,
  triggerResolutionColumns,
} from './columnDefs';

const Reports = () => {
  //useState Hooks
  const [rowData, setRowData] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportLabel, setReportLabel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [disabledFilters, setDisabledFilter] = useState([]);
  const [selectedMapName, setSelectedMapName] = useState(''); //used for gateway report

  const handleMapChange = (mapId, mapName) => {
    setSelectedMapName(mapName); // Update the mapName state
  };

  const ChangeReportType = (reportType, reportLabel) => {
    setReportType(reportType);
    setReportLabel(reportLabel);
  };

  const id = 'analytic-reports';
  const cancelToken = Api.CancelToken;
  let fetchDataCancel;

  const columnDefs = {
    assets: assetColumns,
    battery: batteryColumns,
    environmental: environmentalColumns,
    'gateway-network-uptime': gatewayNetworkColumns,
    'trigger-resolution': triggerResolutionColumns,
  };

  const assetFilters = authService
    .ensureAssetFiltersInLocalStorage()
    .concat(JSON.parse(localStorage.getItem('tempFilters') || '[]'));

  const fetchData = async (payload, csvExport, queryState, pageType) => {
    if (fetchDataCancel !== null && typeof fetchDataCancel === 'function') {
      fetchDataCancel();
      fetchDataCancel = null;
    }

    setIsLoading(true);

    Api.post(`/reports/${reportType}/filtered/`, payload, {
      cancelToken: new cancelToken(function executor(c) {
        fetchDataCancel = c;
      }),
    })
      .then((data) => {
        let resp = data.data;
        setRowData(resp);
        setIsLoading(false);
      })
      .catch((e) => {
        if (!Axios.isCancel(e)) {
          handleTimeoutApiError(e);
          setIsLoading(false);
        }
      });

    // Help create call to export reports
    if (csvExport) {
      const config = {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      };

      Api.post(`/reports/${reportType}/export`, payload, config)
        .then((res) => {
          // Extract and format the start and end dates
          const startDateFilter = payload.filters.find((f) => f.field === 'startdate');
          const endDateFilter = payload.filters.find((f) => f.field === 'enddate');

          const formatDate = (isoDate) => {
            const date = new Date(isoDate);
            const month = date.toLocaleString('en-US', { month: 'short' });
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month}-${day}-${year}`;
          };

          const startDateFormatted = startDateFilter
            ? formatDate(startDateFilter.filter)
            : 'unknown';
          const endDateFormatted = endDateFilter ? formatDate(endDateFilter.filter) : 'unknown';

          const mapNamePart = selectedMapName
            ? selectedMapName.replace(/\s+/g, '_') // Replace spaces with underscores
            : 'unknown_map';

          switch (reportType) {
            case 'battery':
              setCsvFileName('Analytics_Battery_Report_Export');
              setCsvData(res.data);
              break;
            case 'gateway-network-uptime':
              setCsvFileName(
                `${mapNamePart}_GatewayUptimeReport_${startDateFormatted}-${endDateFormatted}`,
              );

              res.data.text().then((text) => {
                const rows = text.split('\n');

                if (rows.length < 2) {
                  setCsvData(res.data);
                  setIsLoading(false);
                  return;
                }

                const headers = rows[0].split(',');

                const uptimeIndex = headers.findIndex((h) => h.toLowerCase().includes('uptime'));

                if (uptimeIndex === -1) {
                  setCsvData(res.data);
                  setIsLoading(false);
                  return;
                }

                const updatedRows = rows.map((row, i) => {
                  const cells = row.split(',');

                  if (i === 0 || !cells[uptimeIndex]) return row;

                  const cleaned = cells[uptimeIndex].replace(/"/g, '').trim();
                  const raw = parseFloat(cleaned);

                  if (!isNaN(raw)) {
                    const percent = (raw * 100).toFixed(1) + '%';

                    cells[uptimeIndex] = percent;
                  }

                  return cells.join(',');
                });

                const finalCsv = updatedRows.join('\n');

                const newBlob = new Blob([finalCsv], {
                  type: 'text/csv',
                });

                setCsvData(newBlob);
                setIsLoading(false);
              });
              break;

            case 'trigger-resolution':
              setCsvFileName('Analytics_Trigger_Resolution_Report_Export');
              setCsvData(res.data);
              break;
            default:
              setCsvData(res.data);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          handleTimeoutApiError(error);
          setIsLoading(false);
        });
    }
  };

  // Set row data to undefined when switching between reports
  useEffect(() => {
    if (rowData?.count) {
      setRowData(0);
    } else {
      setRowData(null);
    }
    setCsvData(null);
  }, [reportType]);

  // Setting Disabled Filters for Reports
  useEffect(() => {
    switch (reportType) {
      case 'battery':
        setDisabledFilter(['all']);
        break;
      case 'environmental':
        setDisabledFilter(['all', 'identifier', 'keyword', 'map', 'place', 'active']);
        break;
      case 'trigger-resolution':
        setDisabledFilter([
          'all',
          'identifier',
          'keyword',
          'map',
          'place',
          'device id',
          'battery value',
        ]);
        break;
      default:
        setDisabledFilter([]);
    }
  }, [reportType]);

  return (
    <>
      <div className="wrapper">
        <ServerSideGrid
          id={id}
          initQueryParams={new PagedQueryParams()}
          columnDefs={columnDefs[reportType]}
          rowData={rowData}
          csvData={csvData}
          csvFileName={csvFileName}
          handleFetchData={fetchData}
          isLoading={isLoading}
          assetFilters={assetFilters}
          dateControl={true}
          filterControl={true}
          disabledFilters={disabledFilters}
          searchControl={true}
          viewingControl={true}
          pagerControl={true}
          exportControl={true}
          ignoreFetch={true}
          dateFilters
          gridProps={{
            frameworkComponents: {
              AssetNameCellRenderer,
              LastLocationCellRenderer,
              LinkCellRenderer,
            },
          }}
          reportType={reportType}
          reportLabel={reportLabel}
          ChangeReportType={ChangeReportType}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          onMapChange={handleMapChange}
        />
      </div>
    </>
  );
};

export default Reports;
